import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Mail, Send, Paperclip, Loader, AlertCircle, Eye, X } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { useFacility } from '../../context/FacilityContext';
import { formatDateTime } from '../../utils/datetime';

// Same body-build behavior as BulkEmail + CustomEmailTemplateModal:
// plain -> wrap paragraphs with inline-styled <p>; html -> pass through.
const escapeHtml = (s) =>
  String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const buildHtml = (raw, mode) => {
  if (mode === 'html') return raw;
  const paragraphs = (raw || '').split(/\n{2,}/);
  return paragraphs
    .map(
      (p) =>
        `<p style="margin: 0 0 16px 0; line-height: 1.5;">${escapeHtml(p).replace(/\n/g, '<br>')}</p>`,
    )
    .join('\n');
};

const htmlToPlain = (html) => {
  if (!html) return '';
  let s = String(html);
  s = s.replace(/<\/(p|div|h[1-6]|li|tr)>/gi, '\n\n');
  s = s.replace(/<(br|hr)\s*\/?>/gi, '\n');
  s = s.replace(/<[^>]+>/g, '');
  s = s
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
  s = s.replace(/\n{3,}/g, '\n\n').trim();
  return s;
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const SourcePill = ({ source }) => {
  const map = {
    manual:     { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Manual' },
    bulk:       { cls: 'bg-blue-50 text-blue-700 border-blue-200',         label: 'Bulk' },
    automation: { cls: 'bg-slate-100 text-slate-700 border-slate-200',     label: 'Automation' },
  };
  const m = map[source] || { cls: 'bg-slate-100 text-slate-600 border-slate-200', label: source };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-medium uppercase tracking-wider border ${m.cls}`}>
      {m.label}
    </span>
  );
};

// Delivery-truth pill: shows the real Resend outcome (delivered/opened/bounced/
// complained) when the webhook has stamped it, else the send state. This is the
// answer to "did the tenant actually get it?" — surfaced without opening Resend.
const DeliveryPill = ({ status }) => {
  const map = {
    delivered:  { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'Delivered' },
    opened:     { cls: 'bg-teal-50 text-teal-700 border-teal-200',          label: 'Seen' },
    clicked:    { cls: 'bg-teal-50 text-teal-700 border-teal-200',          label: 'Seen' },
    sent:       { cls: 'bg-slate-100 text-slate-600 border-slate-200',      label: 'Sent' },
    bounced:    { cls: 'bg-red-50 text-red-700 border-red-200',             label: 'Bounced' },
    complained: { cls: 'bg-red-50 text-red-700 border-red-200',             label: 'Spam report' },
    failed:     { cls: 'bg-red-50 text-red-700 border-red-200',             label: 'Failed' },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-medium border ${m.cls}`}>
      {m.label}
    </span>
  );
};

const EmailTab = ({ customerId, facilityId }) => {
  const { currentFacility } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [bodyMode, setBodyMode] = useState('plain');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const fileInputRef = useRef(null);

  const loadHistory = useCallback(async () => {
    if (!facilityId || !customerId) return;
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const { data } = await apiClient.get(
        `/api/facilities/${facilityId}/customers/${customerId}/communications?channel=email`,
      );
      setHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setHistoryError(
        err?.response?.data?.detail || 'Failed to load email history.',
      );
    } finally {
      setHistoryLoading(false);
    }
  }, [facilityId, customerId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      setSendError('Subject and body are required.');
      return;
    }
    setSendError(null);
    setSending(true);
    try {
      const html = buildHtml(body, bodyMode);
      const payload = {
        subject,
        body_html: html,
        body_text: bodyMode === 'plain' ? body : null,
      };
      if (attachment) {
        const b64 = await fileToBase64(attachment);
        payload.attachment_blob = b64;
        payload.attachment_filename = attachment.name;
        payload.attachment_content_type = attachment.type || null;
      }
      await apiClient.post(
        `/api/facilities/${facilityId}/customers/${customerId}/send-email`,
        payload,
      );
      setSubject('');
      setBody('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadHistory();
    } catch (err) {
      setSendError(
        err?.response?.data?.detail || 'Failed to send email.',
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      {/* Compose */}
      <div className="rounded-xl border border-slate-200/80 bg-white p-4 mb-5">
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-blue-600" />
          <h3 className="text-[14px] font-semibold text-slate-900">
            Send email
          </h3>
        </div>

        <div className="mb-3">
          <label className="block text-[12px] font-medium text-slate-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject"
            className="w-full border border-slate-300 rounded-md px-3 py-1.5 text-[13px] focus:outline-none focus:border-blue-500"
          />
        </div>

        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <label className="block text-[12px] font-medium text-slate-700">
              Body
            </label>
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
              <button
                type="button"
                onClick={() => {
                  if (bodyMode !== 'plain') setBody(htmlToPlain(body));
                  setBodyMode('plain');
                }}
                className={`px-3 py-0.5 text-[11px] font-medium ${
                  bodyMode === 'plain' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                Plain text
              </button>
              <button
                type="button"
                onClick={() => {
                  if (bodyMode !== 'html') setBody(buildHtml(body, 'plain'));
                  setBodyMode('html');
                }}
                className={`px-3 py-0.5 text-[11px] font-medium ${
                  bodyMode === 'html' ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                HTML
              </button>
            </div>
          </div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={6}
            placeholder={
              bodyMode === 'html'
                ? '<p>Hi {{tenant_name}},</p>'
                : 'Hi there,\n\nLine breaks become paragraphs.'
            }
            className={`w-full border border-slate-300 rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-blue-500 ${
              bodyMode === 'html' ? 'font-mono' : ''
            }`}
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              onChange={(e) => setAttachment(e.target.files?.[0] || null)}
              className="hidden"
              id="email-tab-attachment"
            />
            <label
              htmlFor="email-tab-attachment"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-200 text-[12px] text-slate-700 hover:bg-slate-50 cursor-pointer"
            >
              <Paperclip className="h-3.5 w-3.5" />
              {attachment ? attachment.name : 'Attach file'}
            </label>
            {attachment && (
              <button
                type="button"
                onClick={() => {
                  setAttachment(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-[12px] text-slate-400 hover:text-slate-600"
              >
                Remove
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              disabled={!subject.trim() || !body.trim()}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Eye className="h-3.5 w-3.5" /> Preview
            </button>
            <button
              type="button"
              onClick={handleSend}
              disabled={sending}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {sending ? (
                <><Loader className="h-3.5 w-3.5 animate-spin" /> Sending...</>
              ) : (
                <><Send className="h-3.5 w-3.5" /> Send</>
              )}
            </button>
          </div>
        </div>

        {sendError && (
          <div className="mt-3 inline-flex items-center gap-1.5 text-[12px] text-red-600">
            <AlertCircle className="h-3.5 w-3.5" /> {sendError}
          </div>
        )}
      </div>

      {/* History */}
      <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
        <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
          <h3 className="text-[14px] font-semibold text-slate-900">
            Email history
          </h3>
          <span className="text-[12px] text-slate-400">
            {historyLoading ? '...' : `${history.length} message${history.length === 1 ? '' : 's'}`}
          </span>
        </div>
        {historyError && (
          <div className="px-4 py-3 text-[13px] text-red-600">{historyError}</div>
        )}
        {historyLoading ? (
          <div className="py-10 flex items-center justify-center">
            <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : history.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-slate-400">
            No emails sent yet.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {history.map((row) => {
              const expanded = expandedId === row.id;
              return (
                <li
                  key={row.id}
                  className="px-4 py-3 cursor-pointer hover:bg-slate-50/60"
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                >
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div className="flex items-center gap-2 min-w-0">
                      <SourcePill source={row.source} />
                      <span className="text-[12px] text-slate-500">
                        {formatDateTime(row.sent_at, timeZone)}
                      </span>
                      <span className="text-[11px] text-slate-400 truncate">
                        {row.source_label}
                      </span>
                    </div>
                    <DeliveryPill status={row.status} />
                  </div>
                  <div className="text-[13px] font-medium text-slate-900 truncate">
                    {row.subject || '(no subject)'}
                  </div>
                  {(row.delivered_at || row.opened_at || row.bounced_at) && (
                    <div className="text-[11px] text-slate-400">
                      {row.bounced_at
                        ? `Bounced ${formatDateTime(row.bounced_at, timeZone)}`
                        : row.opened_at
                        ? `Seen ${formatDateTime(row.opened_at, timeZone)}`
                        : `Delivered ${formatDateTime(row.delivered_at, timeZone)}`}
                    </div>
                  )}
                  {!expanded && (
                    <div className="text-[12px] text-slate-500 truncate">
                      {row.body_preview || '(content not captured)'}
                    </div>
                  )}
                  {expanded && (
                    <div className="mt-2 p-3 rounded-md bg-slate-50 border border-slate-200/80">
                      {row.body_full ? (
                        <div
                          className="text-[13px] text-slate-700"
                          dangerouslySetInnerHTML={{ __html: row.body_full }}
                        />
                      ) : (
                        <p className="text-[12px] text-slate-400">
                          (content not captured)
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Preview modal */}
      {previewOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
                <Eye className="h-4 w-4 text-blue-600" />
                Email preview
              </div>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Close preview"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              {/* Subject metadata */}
              <div className="mb-3 pb-3 border-b border-slate-100">
                <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">
                  Subject
                </div>
                <div className="text-[13px] font-medium text-slate-900">
                  {subject || '(no subject)'}
                </div>
              </div>
              {/* Rendered body */}
              <div
                className="text-[13px] text-slate-800 leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: buildHtml(body, bodyMode) || '<p style="color:#94A3B8;">(no body)</p>',
                }}
              />
              {attachment && (
                <div className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-slate-500">
                  <Paperclip className="h-3.5 w-3.5" />
                  {attachment.name || attachment.filename}
                </div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="inline-flex items-center px-3.5 py-2 rounded-lg border border-slate-300 bg-slate-200 text-[13px] font-medium text-slate-700 hover:bg-slate-300 transition-colors"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => {
                  setPreviewOpen(false);
                  handleSend();
                }}
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Send className="h-3.5 w-3.5" /> Send
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTab;
